// Assembly catalog (§5.8). Each device type expands into a list of catalog parts
// x per-unit quantities, with material cost (source + date) and NECA MLU / BICSI
// labor unit hours (source + date). This is the seed knowledge base — every value
// is editable/source-stamped (admin UI) and material costs are priced from 3DTSI's
// own rate library (SMARTPLANS device baselines + historical Procore products).
//
// COMPLETE INSTALL KITS: each device assembly carries EVERYTHING needed to install
// that device at its location — the device, its mounting/back box, the home-run
// cable, connectors/jacks/terminations, cable supports (J-hooks), labels, and
// test/certify. Shared head-end / IDF infrastructure (data racks, ladder rack,
// patch panels, PoE switches, grounding) is added separately and scaled to the
// system size by the BOM generator (deriveInfrastructure), since its quantity is
// design-dependent, not 1:1 with a counted device.

export type LaborCategory = 'electrician' | 'sound_comm' | 'technician';

export interface CatalogPart {
  part_number: string;
  manufacturer: string;
  description: string;
  unit: string;
  material_cost: number;     // USD; SMARTPLANS:* and real part numbers reprice exactly
  qty_per: number;           // quantity of this part per one device
  labor_hours: number;       // NECA MLU / BICSI install hours for qty_per
  labor_source: string;      // 'NECA MLU' | 'BICSI' | 'in-house'
}

export interface Assembly {
  device_key: string;        // matches DEVICE_LIBRARY key
  labor_category: LaborCategory;
  parts: CatalogPart[];
}

// Compact builder: [part_number, manufacturer, description, unit, material_cost, qty_per, labor_hours, labor_source]
type P = [string, string, string, string, number, number, number, string];
const part = (p: P): CatalogPart => ({
  part_number: p[0], manufacturer: p[1], description: p[2], unit: p[3],
  material_cost: p[4], qty_per: p[5], labor_hours: p[6], labor_source: p[7],
});
const asm = (device_key: string, labor_category: LaborCategory, parts: P[]): Assembly =>
  ({ device_key, labor_category, parts: parts.map(part) });

// ── Reusable install-kit parts (real rate-library items) ──────────────────────
const MUDRING: P = ['SMARTPLANS:structuredCabling.pathway.mud_ring_1g', '', '1-gang mud ring / plaster ring', 'ea', 1.5, 1, 0.08, 'BICSI'];
const BOX4SQ: P = ['SMARTPLANS:structuredCabling.pathway.box_4sq', '', '4" square box w/ ring', 'ea', 4, 1, 0.15, 'NECA MLU'];
const JHOOK: P = ['SMARTPLANS:structuredCabling.pathway.jhook_2in', '', 'J-hook cable support (per run share)', 'ea', 3.5, 2, 0.10, 'BICSI'];
const LABEL: P = ['SMARTPLANS:structuredCabling.testing.cable_labels', '', 'Cable labels (both ends)', 'ea', 0.22, 2, 0.02, 'BICSI'];
const TEST_DROP: P = ['TEST-CERT', 'Fluke', 'Test / certify (per drop)', 'ea', 1, 1, 0.15, 'BICSI'];
const TERM_LV: P = ['TERM-LV', 'Generic', 'Terminations & connectors', 'ea', 6, 1, 0.30, 'NECA MLU'];
const RJ45: P = ['RJ45-CAT6', 'Panduit', 'RJ45 field plug (device end)', 'ea', 1.2, 1, 0.10, 'BICSI'];
const JACK6: P = ['CJ688TGBU', 'Panduit', 'Cat6 keystone jack', 'ea', 8, 1, 0.12, 'BICSI'];
const JACK6A: P = ['CJA688TGBU', 'Panduit', 'Cat6A keystone jack', 'ea', 14, 1, 0.15, 'BICSI'];
const FP2: P = ['CFPL2WHY', 'Panduit', '2-port faceplate', 'ea', 3.5, 1, 0.08, 'BICSI'];

export const ASSEMBLY_LIBRARY: Assembly[] = [
  // ── Access Control ── full reader/device kits
  asm('access.card_reader', 'electrician', [
    ['40TKS-00-000000', 'HID', 'Signo 40 multi-tech wall-mount reader', 'ea', 241, 1, 0.75, 'NECA MLU'],
    MUDRING, BOX4SQ,
    ['CBL-22-6', 'WestPenn', 'Access control composite cable (6-cond, home run)', 'ft', 0.55, 75, 0.55, 'BICSI'],
    JHOOK, TERM_LV, LABEL,
  ]),
  asm('access.door_contact', 'electrician', [
    ['SMARTPLANS:accessControl.hardware.door_contact', '', 'Door position switch / contact', 'ea', 16, 1, 0.30, 'NECA MLU'],
    ['CBL-22-2', 'WestPenn', '22/2 lead-in cable', 'ft', 0.28, 30, 0.25, 'BICSI'],
    TERM_LV, LABEL,
  ]),
  asm('access.rex', 'electrician', [
    ['SMARTPLANS:accessControl.hardware.rex_pir', '', 'Request-to-exit motion sensor (PIR)', 'ea', 35, 1, 0.50, 'NECA MLU'],
    BOX4SQ,
    ['CBL-22-4', 'WestPenn', '22/4 cable', 'ft', 0.34, 35, 0.30, 'BICSI'],
    TERM_LV, LABEL,
  ]),
  asm('access.lock', 'electrician', [
    ['SMARTPLANS:accessControl.hardware.electric_strike', '', 'Electric strike (fail-secure/fail-safe)', 'ea', 140, 1, 1.50, 'NECA MLU'],
    ['SMARTPLANS:accessControl.hardware.power_transfer_hinge', '', 'Electrified power transfer hinge', 'ea', 120, 1, 0.75, 'NECA MLU'],
    ['CBL-18-4', 'WestPenn', '18/4 lock power cable', 'ft', 0.45, 40, 0.35, 'BICSI'],
    TERM_LV, LABEL,
  ]),
  asm('access.controller', 'electrician', [
    ['SMARTPLANS:accessControl.panels.panel_4door', '', 'Access control panel (4-door)', 'ea', 720, 1, 2.50, 'NECA MLU'],
    ['SMARTPLANS:accessControl.panels.power_supply_4a', '', 'Power supply 12/24VDC 4A w/ battery', 'ea', 180, 1, 1.50, 'NECA MLU'],
    ['SMARTPLANS:accessControl.panels.battery_7ah', '', 'Backup battery 12V 7Ah', 'ea', 28, 1, 0.20, 'NECA MLU'],
    ['SMARTPLANS:accessControl.panels.enclosure_large', '', 'Panel enclosure', 'ea', 85, 1, 0.75, 'NECA MLU'],
    TERM_LV, LABEL,
  ]),
  asm('access.intercom', 'sound_comm', [
    ['SMARTPLANS:accessControl.intercom.intercom_station', '', 'IP intercom / call station (SIP)', 'ea', 450, 1, 1.25, 'NECA MLU'],
    BOX4SQ,
    ['CBL-CAT6', 'WestPenn', 'Cat6 station cable (home run)', 'ft', 0.30, 120, 0.65, 'BICSI'],
    JHOOK, JACK6, RJ45, TERM_LV, LABEL,
  ]),

  // ── CCTV ── full camera kits (camera + mount + drop + connectors + license)
  asm('cctv.fixed_dome', 'sound_comm', [
    ['10232500', 'Axis Communications', 'P3265-LV fixed dome camera 2MP', 'ea', 451.82, 1, 0.90, 'NECA MLU'],
    ['SMARTPLANS:cctv.accessories.junction_box', '', 'Camera junction / back box', 'ea', 28, 1, 0.30, 'NECA MLU'],
    ['CBL-CAT6', 'WestPenn', 'Cat6 camera drop (home run)', 'ft', 0.30, 140, 0.75, 'BICSI'],
    JHOOK, RJ45, JACK6, LABEL, TEST_DROP,
    ['SMARTPLANS:cctv.recording.vms_license', '', 'VMS software license (per camera)', 'ea', 45, 1, 0.10, 'in-house'],
  ]),
  asm('cctv.ptz', 'sound_comm', [
    ['CAM-PTZ', 'AXIS', 'PTZ IP camera (allowance - confirm model)', 'ea', 1450, 1, 1.50, 'NECA MLU'],
    ['SMARTPLANS:cctv.accessories.pole_mount_arm', '', 'Camera pole / wall mount arm', 'ea', 120, 1, 0.75, 'NECA MLU'],
    ['CBL-CAT6', 'WestPenn', 'Cat6 camera drop (home run)', 'ft', 0.30, 160, 0.85, 'BICSI'],
    JHOOK, RJ45, JACK6, LABEL, TEST_DROP,
    ['SMARTPLANS:cctv.recording.vms_license', '', 'VMS software license (per camera)', 'ea', 45, 1, 0.10, 'in-house'],
  ]),
  asm('cctv.bullet', 'sound_comm', [
    ['M2036-LE Mk II', 'Axis', 'Fixed bullet camera 2-4MP', 'ea', 390, 1, 0.90, 'NECA MLU'],
    ['SMARTPLANS:cctv.accessories.junction_box', '', 'Camera junction / back box', 'ea', 28, 1, 0.30, 'NECA MLU'],
    ['CBL-CAT6', 'WestPenn', 'Cat6 camera drop (home run)', 'ft', 0.30, 140, 0.75, 'BICSI'],
    JHOOK, RJ45, JACK6, LABEL, TEST_DROP,
    ['SMARTPLANS:cctv.recording.vms_license', '', 'VMS software license (per camera)', 'ea', 45, 1, 0.10, 'in-house'],
  ]),
  asm('cctv.multisensor', 'sound_comm', [
    ['02634-001', 'Axis Communications', 'P3737-PLE panoramic / multisensor camera', 'ea', 1240.27, 1, 1.50, 'NECA MLU'],
    ['SMARTPLANS:cctv.accessories.junction_box', '', 'Camera junction / back box', 'ea', 28, 1, 0.40, 'NECA MLU'],
    ['CBL-CAT6', 'WestPenn', 'Cat6 camera drop (home run)', 'ft', 0.30, 140, 0.75, 'BICSI'],
    JHOOK, RJ45, JACK6, LABEL, TEST_DROP,
    ['SMARTPLANS:cctv.recording.vms_license', '', 'VMS software license (per camera)', 'ea', 45, 2, 0.10, 'in-house'],
  ]),

  // ── Intrusion ──
  asm('intrusion.motion', 'electrician', [
    ['SMARTPLANS:intrusionDetection.sensors.motion_dual', '', 'Dual-tech motion detector (PIR+MW)', 'ea', 85, 1, 0.50, 'NECA MLU'],
    ['CBL-22-4', 'WestPenn', '22/4 cable', 'ft', 0.34, 45, 0.35, 'BICSI'],
    JHOOK, TERM_LV, LABEL,
  ]),
  asm('intrusion.glassbreak', 'electrician', [
    ['SMARTPLANS:intrusionDetection.sensors.glass_break', '', 'Glass-break detector (acoustic)', 'ea', 48, 1, 0.50, 'NECA MLU'],
    ['CBL-22-4', 'WestPenn', '22/4 cable', 'ft', 0.34, 45, 0.35, 'BICSI'],
    JHOOK, TERM_LV, LABEL,
  ]),
  asm('intrusion.keypad', 'electrician', [
    ['SMARTPLANS:intrusionDetection.panels.keypad_touch', '', 'Touchscreen intrusion keypad', 'ea', 180, 1, 0.75, 'NECA MLU'],
    MUDRING,
    ['CBL-22-6', 'WestPenn', '22/6 cable', 'ft', 0.55, 60, 0.45, 'BICSI'],
    TERM_LV, LABEL,
  ]),

  // ── Fire Alarm ── device + base/box + SLC/NAC wire + support + label
  asm('fa.smoke', 'electrician', [
    ['SMARTPLANS:fireAlarm.initiating.smoke_photo', '', 'Photoelectric smoke detector (addressable)', 'ea', 55, 1, 0.50, 'NECA MLU'],
    ['SMARTPLANS:fireAlarm.initiating.detector_base', '', 'Detector base / mounting base', 'ea', 12, 1, 0.10, 'NECA MLU'],
    ['SMARTPLANS:fireAlarm.wire.fa_wire_182', '', 'FPLP SLC wire 18/2', 'ft', 0.12, 45, 0.40, 'NECA MLU'],
    JHOOK, LABEL,
  ]),
  asm('fa.heat', 'electrician', [
    ['SMARTPLANS:fireAlarm.initiating.heat_ror', '', 'Rate-of-rise heat detector (addressable)', 'ea', 52, 1, 0.50, 'NECA MLU'],
    ['SMARTPLANS:fireAlarm.initiating.detector_base', '', 'Detector base / mounting base', 'ea', 12, 1, 0.10, 'NECA MLU'],
    ['SMARTPLANS:fireAlarm.wire.fa_wire_182', '', 'FPLP SLC wire 18/2', 'ft', 0.12, 45, 0.40, 'NECA MLU'],
    JHOOK, LABEL,
  ]),
  asm('fa.duct', 'electrician', [
    ['SMARTPLANS:fireAlarm.initiating.duct_detector', '', 'Duct smoke detector w/ housing, test/reset', 'ea', 220, 1, 1.25, 'NECA MLU'],
    ['RTS-FA', 'System Sensor', 'Remote test/reset station', 'ea', 48, 1, 0.40, 'NECA MLU'],
    ['SMARTPLANS:fireAlarm.wire.fa_wire_182', '', 'FPLP SLC wire 18/2', 'ft', 0.12, 55, 0.45, 'NECA MLU'],
    JHOOK, LABEL,
  ]),
  asm('fa.pull', 'electrician', [
    ['SMARTPLANS:fireAlarm.initiating.pull_station', '', 'Manual pull station (addressable)', 'ea', 55, 1, 0.50, 'NECA MLU'],
    BOX4SQ,
    ['SMARTPLANS:fireAlarm.wire.fa_wire_182', '', 'FPLP SLC wire 18/2', 'ft', 0.12, 40, 0.35, 'NECA MLU'],
    JHOOK, LABEL,
  ]),
  asm('fa.horn_strobe', 'electrician', [
    ['SMARTPLANS:fireAlarm.notification.horn_strobe_wall', '', 'Horn/strobe (wall mount)', 'ea', 55, 1, 0.50, 'NECA MLU'],
    BOX4SQ,
    ['SMARTPLANS:fireAlarm.wire.fa_wire_144', '', 'FPLP NAC wire 14/4', 'ft', 0.22, 45, 0.40, 'NECA MLU'],
    JHOOK, LABEL,
  ]),
  asm('fa.strobe', 'electrician', [
    ['SMARTPLANS:fireAlarm.notification.strobe_only', '', 'Strobe only (visual notification)', 'ea', 48, 1, 0.50, 'NECA MLU'],
    BOX4SQ,
    ['SMARTPLANS:fireAlarm.wire.fa_wire_144', '', 'FPLP NAC wire 14/4', 'ft', 0.22, 45, 0.40, 'NECA MLU'],
    JHOOK, LABEL,
  ]),
  asm('fa.speaker', 'electrician', [
    ['SMARTPLANS:fireAlarm.notification.speaker_strobe', '', 'Speaker/strobe (voice evac)', 'ea', 105, 1, 0.60, 'NECA MLU'],
    BOX4SQ,
    ['SMARTPLANS:fireAlarm.wire.fa_wire_144', '', 'FPLP audio/NAC wire 14/4', 'ft', 0.22, 50, 0.45, 'NECA MLU'],
    JHOOK, LABEL,
  ]),
  asm('fa.module', 'electrician', [
    ['SMARTPLANS:fireAlarm.panels.monitor_module', '', 'Monitor / control module (addressable)', 'ea', 42, 1, 0.50, 'NECA MLU'],
    BOX4SQ,
    ['SMARTPLANS:fireAlarm.wire.fa_wire_182', '', 'FPLP SLC wire 18/2', 'ft', 0.12, 30, 0.30, 'NECA MLU'],
    LABEL,
  ]),
  asm('fa.facp', 'electrician', [
    ['SMARTPLANS:fireAlarm.panels.facp_small', '', 'Fire alarm control panel (small, 125 pts)', 'ea', 2500, 1, 8.0, 'NECA MLU'],
    ['SMARTPLANS:fireAlarm.panels.battery_facp', '', 'FACP battery set (2x 12V)', 'set', 75, 1, 0.50, 'NECA MLU'],
    ['SMARTPLANS:fireAlarm.panels.annunciator', '', 'Remote annunciator', 'ea', 650, 1, 1.5, 'NECA MLU'],
    TERM_LV, LABEL,
  ]),

  // ── Structured Cabling / Network ── complete drop kit
  asm('cabling.data_drop', 'sound_comm', [
    ['CBL-6A', 'CommScope', 'Cat6A plenum cable (home run)', 'ft', 0.42, 140, 0.65, 'BICSI'],
    ['CJA688TGBU', 'Panduit', 'Cat6A keystone jack (2 per drop)', 'ea', 14, 2, 0.24, 'BICSI'],
    FP2, MUDRING, JHOOK, LABEL, TEST_DROP,
  ]),
  asm('cabling.wap', 'sound_comm', [
    ['SMARTPLANS:structuredCabling.wap.wap_indoor', '', 'Wireless access point (Wi-Fi 6/6E)', 'ea', 380, 1, 0.50, 'BICSI'],
    ['SMARTPLANS:structuredCabling.wap.wap_mount', '', 'WAP ceiling mount bracket', 'ea', 15, 1, 0.30, 'BICSI'],
    ['CBL-6A', 'CommScope', 'Cat6A WAP drop (home run)', 'ft', 0.42, 160, 0.70, 'BICSI'],
    JACK6A, RJ45, JHOOK, LABEL, TEST_DROP,
  ]),
  asm('cabling.rack', 'sound_comm', [
    ['SMARTPLANS:structuredCabling.racks.rack_4post', '', '4-post equipment rack 42RU', 'ea', 480, 1, 3.0, 'BICSI'],
    ['CP48688TBL', 'Panduit', '48-port Cat6/6A patch panel', 'ea', 150, 2, 1.0, 'BICSI'],
    ['SMARTPLANS:structuredCabling.racks.pdu_basic', '', 'PDU vertical 20A', 'ea', 85, 2, 0.5, 'BICSI'],
    ['SMARTPLANS:structuredCabling.racks.cable_mgmt_vert', '', 'Vertical cable manager (pair)', 'ea', 65, 1, 0.5, 'BICSI'],
    ['SMARTPLANS:structuredCabling.racks.cable_mgmt_horiz', '', 'Horizontal cable manager 1RU', 'ea', 28, 4, 0.3, 'BICSI'],
    ['SMARTPLANS:structuredCabling.grounding.tgb', '', 'Telecom grounding busbar', 'ea', 85, 1, 0.5, 'BICSI'],
    ['SMARTPLANS:structuredCabling.racks.shelf_rack', '', 'Rack shelf 1RU', 'ea', 45, 1, 0.2, 'BICSI'],
  ]),
  asm('cabling.tv', 'sound_comm', [
    ['CBL-RG6', 'CommScope', 'RG6 plenum coax (home run)', 'ft', 0.35, 110, 0.55, 'BICSI'],
    ['FP-TV', 'Panduit', 'TV faceplate w/ F-connector', 'ea', 7, 1, 0.20, 'BICSI'],
    MUDRING, JHOOK, LABEL,
  ]),

  // ── PA / Nurse Call ──
  asm('pa.speaker', 'sound_comm', [
    ['SMARTPLANS:audioVisual.audio.speaker_ceiling', '', 'Ceiling speaker 70V/100V', 'ea', 85, 1, 0.50, 'NECA MLU'],
    ['CBL-18-2', 'WestPenn', '18/2 speaker cable (home run)', 'ft', 0.30, 70, 0.45, 'BICSI'],
    BOX4SQ, JHOOK, TERM_LV, LABEL,
  ]),
  asm('nursecall.station', 'sound_comm', [
    ['SMARTPLANS:nurseCall.stations.patient_station', '', 'Patient bedside station', 'ea', 180, 1, 1.0, 'NECA MLU'],
    ['SMARTPLANS:nurseCall.wire.nc_cable', '', 'Nurse call cable (shielded multipair)', 'ft', 0.20, 90, 0.65, 'BICSI'],
    MUDRING, JHOOK, TERM_LV, LABEL,
  ]),

  // ── Audio-Video / Digital Signage ──
  asm('av.display', 'sound_comm', [
    ['SMARTPLANS:audioVisual.displays.display_55', '', '55" commercial display', 'ea', 850, 1, 1.5, 'NECA MLU'],
    ['SMARTPLANS:audioVisual.displays.display_mount', '', 'TV/display wall mount', 'ea', 95, 1, 0.75, 'NECA MLU'],
    ['SMARTPLANS:audioVisual.infrastructure.hdmi_over_cat', '', 'HDMI over Cat extender (TX+RX)', 'ea', 180, 1, 0.40, 'BICSI'],
    ['SMARTPLANS:audioVisual.infrastructure.wall_plate_av', '', 'AV wall plate', 'ea', 35, 1, 0.20, 'NECA MLU'],
    BOX4SQ, ['CBL-6A', 'CommScope', 'Cat6A signal/control drop', 'ft', 0.42, 60, 0.40, 'BICSI'],
    JHOOK, LABEL,
  ]),
  asm('av.projector', 'sound_comm', [
    ['SMARTPLANS:audioVisual.displays.projector_std', '', 'Projector (standard throw 4000lm)', 'ea', 950, 1, 2.0, 'NECA MLU'],
    ['SMARTPLANS:audioVisual.displays.projector_mount', '', 'Projector ceiling mount', 'ea', 120, 1, 1.0, 'NECA MLU'],
    ['SMARTPLANS:audioVisual.infrastructure.hdmi_over_cat', '', 'HDMI over Cat extender (TX+RX)', 'ea', 180, 1, 0.40, 'BICSI'],
    ['CBL-6A', 'CommScope', 'Cat6A signal/control drop', 'ft', 0.42, 90, 0.50, 'BICSI'],
    JHOOK, LABEL,
  ]),
  asm('av.projection_screen', 'sound_comm', [
    ['SMARTPLANS:audioVisual.displays.screen_electric', '', 'Projection screen (motorized)', 'ea', 650, 1, 2.0, 'NECA MLU'],
    BOX4SQ, ['CBL-CTRL', 'Generic', 'Screen control cable', 'ft', 0.30, 45, 0.30, 'BICSI'],
    TERM_LV, LABEL,
  ]),
  asm('av.speaker', 'sound_comm', [
    ['SMARTPLANS:audioVisual.audio.speaker_wall', '', 'Wall-mount speaker', 'ea', 120, 1, 0.60, 'NECA MLU'],
    BOX4SQ, ['CBL-16-2', 'WestPenn', '16/2 speaker cable (home run)', 'ft', 0.35, 70, 0.45, 'BICSI'],
    JHOOK, TERM_LV, LABEL,
  ]),
  asm('av.microphone', 'sound_comm', [
    ['SMARTPLANS:audioVisual.audio.mic_wireless', '', 'Wireless microphone system', 'ea', 450, 1, 1.0, 'NECA MLU'],
    ['CBL-MIC', 'WestPenn', 'Mic/antenna cable', 'ft', 0.40, 50, 0.35, 'BICSI'],
    TERM_LV, LABEL,
  ]),
  asm('av.assistive_listening', 'sound_comm', [
    ['ALS-TX', 'Williams AV', 'Assistive-listening transmitter + antenna (allowance)', 'ea', 640, 1, 1.0, 'NECA MLU'],
    ['CBL-COAX', 'Generic', 'Antenna coax', 'ft', 0.35, 50, 0.35, 'BICSI'],
    TERM_LV, LABEL,
  ]),
  asm('av.control', 'sound_comm', [
    ['SMARTPLANS:audioVisual.control.keypad_av', '', 'AV control panel / keypad', 'ea', 280, 1, 1.0, 'NECA MLU'],
    ['SMARTPLANS:audioVisual.infrastructure.wall_plate_av', '', 'AV wall plate (HDMI, USB-C)', 'ea', 35, 1, 0.30, 'NECA MLU'],
    BOX4SQ, ['CBL-AV', 'Generic', 'AV/control cable', 'ft', 0.45, 60, 0.40, 'BICSI'],
    TERM_LV, LABEL,
  ]),

  // ── DAS / ERRCS ── (head-end-heavy; allowances where rate library has no baseline)
  asm('das.antenna', 'sound_comm', [
    ['ANT-DAS', 'CommScope', 'In-building DAS antenna (allowance)', 'ea', 180, 1, 1.0, 'NECA MLU'],
    ['CBL-COAX-LDF', 'CommScope', 'Low-loss coax (home run)', 'ft', 1.2, 110, 0.75, 'BICSI'],
    ['MNT-ANT', 'Generic', 'Antenna mount', 'ea', 30, 1, 0.40, 'NECA MLU'],
    JHOOK, TERM_LV, LABEL,
  ]),
  asm('das.amplifier', 'sound_comm', [
    ['BDA-PS', 'Comba', 'Public-safety BDA / repeater (allowance)', 'ea', 4200, 1, 6.0, 'NECA MLU'],
    ['ENC-NEMA', 'Generic', 'NEMA-4 enclosure + battery backup', 'ea', 1800, 1, 3.0, 'NECA MLU'],
    TERM_LV, LABEL,
  ]),
  asm('das.remote_unit', 'sound_comm', [
    ['DAS-RU', 'JMA', 'DAS fiber remote/master unit (allowance)', 'ea', 2600, 1, 4.0, 'NECA MLU'],
    ['CBL-FIBER', 'CommScope', 'OS2 fiber jumper', 'ft', 0.9, 70, 0.55, 'BICSI'],
    TERM_LV, LABEL,
  ]),

  // ── Access accessories ──
  asm('access.door_operator', 'electrician', [
    ['SMARTPLANS:accessControl.hardware.auto_operator', '', 'Automatic door operator (ADA)', 'ea', 2200, 1, 3.0, 'NECA MLU'],
    ['SMARTPLANS:accessControl.hardware.rex_button', '', 'Actuator push plate / button', 'ea', 25, 2, 0.50, 'NECA MLU'],
    BOX4SQ, ['CBL-18-6', 'WestPenn', '18/6 control cable', 'ft', 0.55, 70, 0.45, 'BICSI'],
    TERM_LV, LABEL,
  ]),
  asm('access.release_button', 'electrician', [
    ['SMARTPLANS:accessControl.hardware.rex_button', '', 'Push-to-exit / release button', 'ea', 25, 1, 0.40, 'NECA MLU'],
    MUDRING, ['CBL-18-2', 'WestPenn', '18/2 cable', 'ft', 0.30, 35, 0.25, 'BICSI'],
    TERM_LV, LABEL,
  ]),
  asm('access.vehicle_reader', 'electrician', [
    ['VR-LR', 'HID', 'Long-range / vehicle reader (allowance - confirm model)', 'ea', 560, 1, 1.5, 'NECA MLU'],
    ['MNT-VR', 'Generic', 'Gooseneck / pedestal mount', 'ea', 140, 1, 1.0, 'NECA MLU'],
    ['CBL-AC', 'WestPenn', 'Access composite cable (home run)', 'ft', 0.55, 90, 0.65, 'BICSI'],
    JHOOK, TERM_LV, LABEL,
  ]),

  // ── Intrusion accessory ──
  asm('intrusion.duress', 'electrician', [
    ['DUR-PB', 'Bosch', 'Duress / hold-up button (allowance)', 'ea', 42, 1, 0.40, 'NECA MLU'],
    MUDRING, ['CBL-22-4', 'WestPenn', '22/4 cable', 'ft', 0.34, 45, 0.30, 'BICSI'],
    TERM_LV, LABEL,
  ]),

  // ── Voice / Telephony ──
  asm('comms.emergency_phone', 'sound_comm', [
    ['PHN-EM', 'Talkaphone', 'Emergency / AOR phone (allowance)', 'ea', 680, 1, 1.5, 'NECA MLU'],
    BOX4SQ, ['CBL-CAT6', 'WestPenn', 'Station cable (home run)', 'ft', 0.30, 120, 0.65, 'BICSI'],
    JHOOK, JACK6, RJ45, TERM_LV, LABEL,
  ]),

  // ── Infrastructure / boxes ──
  asm('infra.junction_box', 'electrician', [
    ['SMARTPLANS:cctv.accessories.junction_box', '', 'Junction / back box', 'ea', 28, 1, 0.40, 'NECA MLU'],
    ['SMARTPLANS:structuredCabling.pathway.firestop_putty', '', 'Firestop (per penetration)', 'ea', 14, 1, 0.20, 'BICSI'],
    LABEL,
  ]),
  asm('infra.pull_box', 'electrician', [
    ['PB-VAULT', 'Generic', 'Pull box / vault / hand hole (allowance)', 'ea', 220, 1, 2.0, 'NECA MLU'],
    ['SMARTPLANS:structuredCabling.grounding.ground_lug', '', 'Grounding / bonding', 'ea', 6, 1, 0.20, 'NECA MLU'],
    LABEL,
  ]),
  asm('infra.equipment_box', 'sound_comm', [
    ['SMARTPLANS:audioVisual.infrastructure.av_table_box', '', 'Equipment / table / floor box', 'ea', 180, 1, 1.0, 'BICSI'],
    ['CBL-MGMT', 'Generic', 'Cable management / grommets', 'ea', 20, 1, 0.30, 'BICSI'],
    LABEL,
  ]),

  // ── Fiber Backbone / Riser ──
  asm('cabling.fiber_outlet', 'sound_comm', [
    ['FO-LC-DUP', 'Corning', 'LC duplex fiber outlet + pigtails', 'ea', 38, 1, 0.50, 'BICSI'],
    ['CBL-FIBER-OM4', 'CommScope', 'OM4/OS2 fiber to the outlet (home run)', 'ft', 0.75, 140, 0.70, 'BICSI'],
    FP2, MUDRING, JHOOK, LABEL, TEST_DROP,
  ]),
  asm('cabling.fiber_backbone', 'sound_comm', [
    ['CBL-OS2-12', 'Corning', 'OS2 12-strand backbone fiber (riser/OSP)', 'ft', 1.85, 250, 1.20, 'BICSI'],
    ['FIB-LIU', 'Corning', 'Fiber panel / LIU + splice tray', 'ea', 240, 2, 1.5, 'BICSI'],
    ['FIB-LC', 'Corning', 'LC fusion splice-on connector', 'ea', 9, 24, 0.10, 'BICSI'],
    ['TEST-OTDR', 'Fluke', 'OTDR bidirectional certification (per strand)', 'ea', 2, 12, 0.15, 'BICSI'],
    JHOOK, LABEL,
  ]),

  // ── Data Centers ──
  asm('datacenter.cabinet', 'sound_comm', [
    ['SMARTPLANS:structuredCabling.racks.rack_4post_enclosed', '', 'Server cabinet 42RU (enclosed, fans)', 'ea', 750, 1, 4.0, 'BICSI'],
    ['SMARTPLANS:structuredCabling.racks.pdu_metered', '', 'Metered/switched PDU (A+B)', 'ea', 250, 2, 0.6, 'BICSI'],
    ['CP48688TBL', 'Panduit', '48-port patch panel', 'ea', 150, 2, 1.0, 'BICSI'],
    ['SMARTPLANS:structuredCabling.racks.cable_mgmt_vert', '', 'Vertical cable manager (pair)', 'ea', 65, 1, 0.5, 'BICSI'],
    ['SMARTPLANS:structuredCabling.grounding.ground_lug', '', 'Cabinet bonding / ground', 'ea', 6, 2, 0.3, 'BICSI'],
  ]),

  // ── Master Clock / Time ──
  asm('clock.master', 'sound_comm', [
    ['CLK-MASTER', 'American Time', 'Master clock / NTP time server', 'ea', 1200, 1, 2.0, 'NECA MLU'],
    ['CLK-GPS', 'American Time', 'GPS antenna + lead-in', 'ea', 180, 1, 1.0, 'NECA MLU'],
    TERM_LV, LABEL,
  ]),
  asm('clock.secondary', 'sound_comm', [
    ['CLK-SEC', 'American Time', 'Secondary clock (analog/digital, PoE)', 'ea', 95, 1, 0.50, 'NECA MLU'],
    BOX4SQ, ['CBL-CAT6', 'WestPenn', 'Cat6 PoE drop (home run)', 'ft', 0.30, 120, 0.60, 'BICSI'],
    JHOOK, RJ45, LABEL,
  ]),

  // ── Mass Notification / ECS ──
  asm('mnec.speaker', 'electrician', [
    ['SMARTPLANS:fireAlarm.notification.speaker_strobe', '', 'MNS/ECS speaker-strobe (voice)', 'ea', 105, 1, 0.60, 'NECA MLU'],
    BOX4SQ, ['SMARTPLANS:fireAlarm.wire.fa_wire_144', '', 'Survivable audio/NAC wire 14/4', 'ft', 0.22, 50, 0.45, 'NECA MLU'],
    JHOOK, LABEL,
  ]),
  asm('mnec.beacon', 'electrician', [
    ['MNS-BCN', 'Federal Signal', 'MNS LED beacon / warning strobe', 'ea', 95, 1, 0.50, 'NECA MLU'],
    BOX4SQ, ['SMARTPLANS:fireAlarm.wire.fa_wire_144', '', 'Survivable circuit wire 14/4', 'ft', 0.22, 45, 0.40, 'NECA MLU'],
    JHOOK, LABEL,
  ]),
  asm('mnec.panel', 'electrician', [
    ['MNS-NCU', 'Federal Signal', 'MNS/ECS control panel + amplifier', 'ea', 4200, 1, 6.0, 'NECA MLU'],
    ['SMARTPLANS:fireAlarm.panels.battery_facp', '', 'Battery set', 'set', 75, 1, 0.5, 'NECA MLU'],
    TERM_LV, LABEL,
  ]),

  // ── DAS passives ──
  asm('das.splitter', 'sound_comm', [
    ['DAS-SPL', 'CommScope', 'Splitter / directional coupler (passive)', 'ea', 120, 1, 0.50, 'NECA MLU'],
    ['DAS-JMP', 'CommScope', 'Coax jumper', 'ea', 22, 2, 0.20, 'BICSI'],
    ['MNT-PASS', 'Generic', 'Mounting hardware', 'ea', 12, 1, 0.20, 'NECA MLU'],
    LABEL,
  ]),
  asm('das.filter', 'sound_comm', [
    ['DAS-FLT', 'CommScope', 'RF filter / duplexer / cavity', 'ea', 280, 1, 0.60, 'NECA MLU'],
    ['DAS-JMP', 'CommScope', 'Coax jumper', 'ea', 22, 2, 0.20, 'BICSI'],
    ['MNT-PASS', 'Generic', 'Mounting hardware', 'ea', 12, 1, 0.20, 'NECA MLU'],
    LABEL,
  ]),
  asm('das.attenuator', 'sound_comm', [
    ['DAS-ATT', 'CommScope', 'RF attenuator (fixed/variable)', 'ea', 45, 1, 0.30, 'NECA MLU'],
    ['DAS-JMP', 'CommScope', 'Coax jumper', 'ea', 22, 2, 0.20, 'BICSI'],
    LABEL,
  ]),

  // ── Access / Intrusion accessories ──
  asm('access.wireless_pim', 'electrician', [
    ['WPIM', 'Allegion/ASSA', 'Wireless panel interface module / gateway', 'ea', 320, 1, 1.0, 'NECA MLU'],
    ['ENC-SM', 'Generic', 'Enclosure', 'ea', 45, 1, 0.30, 'NECA MLU'],
    ['CBL-CAT6', 'WestPenn', 'Cat6 home run', 'ft', 0.30, 100, 0.55, 'BICSI'],
    JHOOK, TERM_LV, LABEL,
  ]),
  asm('access.delayed_egress', 'electrician', [
    ['DE-HW', 'Von Duprin', 'Delayed-egress panic hardware', 'ea', 1450, 1, 2.5, 'NECA MLU'],
    ['DE-PS', 'Generic', 'Power supply + signage', 'ea', 180, 1, 0.75, 'NECA MLU'],
    ['CBL-18-4', 'WestPenn', '18/4 control cable', 'ft', 0.45, 50, 0.40, 'BICSI'],
    TERM_LV, LABEL,
  ]),
  asm('access.keypad', 'electrician', [
    ['SMARTPLANS:intrusionDetection.panels.keypad_basic', '', 'Access keypad / PIN pad', 'ea', 85, 1, 0.60, 'NECA MLU'],
    MUDRING, ['CBL-22-6', 'WestPenn', '22/6 cable (home run)', 'ft', 0.55, 60, 0.45, 'BICSI'],
    TERM_LV, LABEL,
  ]),

  // ── Intercom (PA) ──
  asm('pa.intercom_master', 'sound_comm', [
    ['ICOM-MS', 'Aiphone', 'Intercom master / call-control station', 'ea', 850, 1, 1.5, 'NECA MLU'],
    ['CBL-CAT6', 'WestPenn', 'System cable (home run)', 'ft', 0.30, 120, 0.65, 'BICSI'],
    JACK6, RJ45, TERM_LV, LABEL,
  ]),
  asm('pa.intercom_call', 'sound_comm', [
    ['ICOM-CS', 'Aiphone', 'Intercom call / sub station', 'ea', 180, 1, 0.75, 'NECA MLU'],
    BOX4SQ, ['CBL-CAT6', 'WestPenn', 'System cable (home run)', 'ft', 0.30, 110, 0.60, 'BICSI'],
    JHOOK, RJ45, LABEL,
  ]),
];

export const ASSEMBLY_BY_DEVICE: Record<string, Assembly> =
  Object.fromEntries(ASSEMBLY_LIBRARY.map((a) => [a.device_key, a]));

// ── Project infrastructure (shared head-end / IDF / pathway) ──────────────────
// Quantity-driven shared items that are NOT 1:1 with a counted device. The BOM
// generator scales these to the system size (counts) and labels them clearly so
// the estimator can adjust. Each rule: given total counts, return part rows.
export interface InfraPart {
  key: string;            // SMARTPLANS / real part number for pricing
  manufacturer: string;
  description: string;
  unit: string;
  unit_cost: number;
  qty: number;
  labor_hours: number;    // total hours for the qty
  labor_category: LaborCategory;
}

export interface CountSummary {
  data_drops: number;     // structured-cabling drops (data outlets)
  poe_devices: number;    // cameras + WAPs + IP intercoms + data drops needing PoE
  cameras: number;
  total_devices: number;  // every counted low-voltage device (for tray/grounding scale)
}

// Returns the shared infrastructure for a project given its device counts.
// Conservative, clearly-labeled, and only emitted when there is something to support.
export function deriveInfrastructure(c: CountSummary): InfraPart[] {
  const out: InfraPart[] = [];
  const ceil = (n: number) => Math.max(0, Math.ceil(n));

  // Head-end / IDF: a data rack + patch panels when there are drops/cameras to land.
  const portCount = c.data_drops + c.cameras + Math.max(0, c.poe_devices - c.cameras);
  if (portCount > 0) {
    const panels = ceil(portCount / 48);
    out.push({ key: 'SMARTPLANS:structuredCabling.racks.rack_4post', manufacturer: '', description: 'Data rack / IDF cabinet (4-post 42RU)', unit: 'ea', unit_cost: 480, qty: Math.max(1, ceil(portCount / 384)), labor_hours: 3 * Math.max(1, ceil(portCount / 384)), labor_category: 'sound_comm' });
    out.push({ key: 'CP48688TBL', manufacturer: 'Panduit', description: '48-port Cat6/6A patch panel', unit: 'ea', unit_cost: 150, qty: panels, labor_hours: 1.0 * panels, labor_category: 'sound_comm' });
    out.push({ key: 'SMARTPLANS:structuredCabling.racks.cable_mgmt_horiz', manufacturer: '', description: 'Horizontal cable manager (per panel)', unit: 'ea', unit_cost: 28, qty: panels, labor_hours: 0.3 * panels, labor_category: 'sound_comm' });
    out.push({ key: 'SMARTPLANS:structuredCabling.grounding.tgb', manufacturer: '', description: 'Telecom grounding busbar (per rack)', unit: 'ea', unit_cost: 85, qty: Math.max(1, ceil(portCount / 384)), labor_hours: 0.5, labor_category: 'sound_comm' });
  }

  // PoE switches sized to PoE device count (48-port managed, ~90% fill).
  if (c.poe_devices > 0) {
    const switches = ceil(c.poe_devices / 44);
    out.push({ key: 'SMARTPLANS:cctv.accessories.poe_switch_48', manufacturer: '', description: 'PoE+ switch 48-port managed (head-end)', unit: 'ea', unit_cost: 1150, qty: switches, labor_hours: 1.0 * switches, labor_category: 'sound_comm' });
  }

  // Pathway: ladder rack + J-hook/tray allowance scaled to device count (cabling.pathway).
  if (c.total_devices > 0) {
    const trayFt = Math.max(20, c.total_devices * 3);   // ~3 ft of overhead pathway per device (allowance)
    out.push({ key: 'SMARTPLANS:structuredCabling.pathway.ladder_rack_12in', manufacturer: '', description: 'Ladder rack / cable runway 12" (allowance, ft)', unit: 'ft', unit_cost: 14, qty: trayFt, labor_hours: 0.06 * trayFt, labor_category: 'sound_comm' });
    out.push({ key: 'SMARTPLANS:structuredCabling.pathway.threaded_rod_10ft', manufacturer: '', description: 'Threaded-rod / support hardware (per 10 ft tray)', unit: 'ea', unit_cost: 5.5, qty: ceil(trayFt / 10), labor_hours: 0.2 * ceil(trayFt / 10), labor_category: 'sound_comm' });
  }

  return out;
}
